import { type IntlShape } from 'react-intl'

import { AFTER_SALES_DAMAGED_PRODUCT_AMOUNT_THRESHOLD } from '../../after-sales'
import { type TENANT } from '../../tenant'

import {
  CONTACT_FORM_SUBJECT_KEY,
  type ContactFormSubjectKey,
  directContactFormSubjectCatalogGetValue,
} from './directContactFormSubjectCatalog'
import {
  CONTACT_FORM_NEUTRAL_SUBJECT_POLICY,
  CONTACT_FORM_SUBJECT_POLICIES,
  type ContactFormFieldRequirement,
  type ContactFormImageUploadPolicy,
  type ContactFormMessageVisibility,
  type ContactFormSubjectPolicy,
} from './directContactFormSubjectPolicy'

export type ResolvedContactFormSubject = {
  key: ContactFormSubjectKey | null
  policy: ContactFormSubjectPolicy
}

export const directContactFormSubjectPolicyGetByTranslatedSubject = ({
  intl,
  subject,
}: {
  intl: IntlShape
  subject: string
}): ResolvedContactFormSubject => {
  const subjectKey = Object.values(CONTACT_FORM_SUBJECT_KEY).find(
    (key) => directContactFormSubjectCatalogGetValue(intl, key) === subject,
  )

  return {
    key: subjectKey ?? null,
    policy: subjectKey
      ? CONTACT_FORM_SUBJECT_POLICIES[subjectKey]
      : CONTACT_FORM_NEUTRAL_SUBJECT_POLICY,
  }
}

export const directContactFormSubjectPolicyIsAvailableForTenant = ({
  subjectKey,
  tenant,
}: {
  subjectKey: ContactFormSubjectKey
  tenant?: TENANT
}): boolean => {
  const policy = CONTACT_FORM_SUBJECT_POLICIES[subjectKey]

  return (
    !policy.availableTenants ||
    (tenant !== undefined && policy.availableTenants.includes(tenant))
  )
}

export const directContactFormSubjectPolicyRequiresLogin = ({
  hasPrefilledOrder,
  policy,
}: {
  hasPrefilledOrder: boolean
  policy: ContactFormSubjectPolicy
}): boolean =>
  policy.loginRequirement === 'always' ||
  (policy.loginRequirement === 'withoutPrefilledOrder' && !hasPrefilledOrder)

export const directContactFormSubjectPolicyShouldShowMessage = ({
  isLoggedIn,
  visibility,
}: {
  isLoggedIn: boolean
  visibility: ContactFormMessageVisibility
}): boolean =>
  visibility === 'always' || (visibility === 'loggedInOnly' && isLoggedIn)

export const directContactFormSubjectPolicyGetImageUploadRequirement = ({
  imageUploadPolicy,
  isLoggedIn,
  selectedProductTotalCost,
}: {
  imageUploadPolicy: ContactFormImageUploadPolicy
  isLoggedIn: boolean
  selectedProductTotalCost: number
}): ContactFormFieldRequirement => {
  if (!isLoggedIn) {
    return 'hidden'
  }

  if (imageUploadPolicy === 'requiredAboveDamagedProductThreshold') {
    return selectedProductTotalCost >
      AFTER_SALES_DAMAGED_PRODUCT_AMOUNT_THRESHOLD
      ? 'required'
      : 'optional'
  }

  return imageUploadPolicy
}
